"use strict";
var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
import { jsx, jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { attachOcrLineDropListeners, forceString } from "./ocr_line_drop";
import { isImeKey } from "../util/ime_key";
const QUERY_DELAY_MS = 250;
const PLACE_DETAIL_FIELDS = ["geometry", "address_components"];
let listCount = 0;
function createPlacesClient() {
  var _a, _b;
  const places = (_b = (_a = window.google) == null ? void 0 : _a.maps) == null ? void 0 : _b.places;
  if (!places) {
    return null;
  }
  return {
    places,
    autocomplete: new places.AutocompleteService(),
    details: new places.PlacesService(document.createElement("div")),
    sessionToken: new places.AutocompleteSessionToken()
  };
}
function suggestionLabel(suggestion, userInput) {
  const { description, matchedSubstring } = suggestion;
  if (!userInput || !matchedSubstring) {
    return description;
  }
  const end = matchedSubstring.offset + matchedSubstring.length;
  return /* @__PURE__ */ jsxs("span", { children: [
    description.slice(0, matchedSubstring.offset),
    /* @__PURE__ */ jsx("b", { className: "geosuggest__item__matched-text", children: description.slice(matchedSubstring.offset, end) }),
    description.slice(end)
  ] });
}
function GeosuggestDroppableInput(props) {
  var _a, _b;
  const {
    initialValue,
    onSuggestionSelected,
    geosuggestClear,
    nonSelect,
    types,
    onKeyDown
  } = props;
  const inputRef = useRef(null);
  const [userInput, setUserInput] = useState(initialValue != null ? initialValue : "");
  const [seededFrom, setSeededFrom] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [activePlaceId, setActivePlaceId] = useState(null);
  const [isHidden, setIsHidden] = useState(true);
  const ignoreBlur = useRef(false);
  const selectedText = useRef(null);
  const queryTimer = useRef(void 0);
  const queryId = useRef(0);
  const selectionId = useRef(0);
  const placesClient = useRef(null);
  const listId = useMemo(() => `geosuggest__list--${++listCount}`, []);
  if (initialValue !== seededFrom) {
    setSeededFrom(initialValue);
    setUserInput(initialValue != null ? initialValue : "");
  }
  const client = () => {
    var _a2;
    (_a2 = placesClient.current) != null ? _a2 : placesClient.current = createPlacesClient();
    return placesClient.current;
  };
  useEffect(() => {
    const input = inputRef.current;
    const applyDroppedChars = (chars) => {
      selectedText.current = null;
      queryId.current += 1;
      selectionId.current += 1;
      flushSync(() => {
        var _a2;
        return setUserInput((_a2 = forceString(chars)) != null ? _a2 : "");
      });
      input.focus();
    };
    const detachOcrLineDrop = attachOcrLineDropListeners(
      input,
      (payload) => applyDroppedChars(payload.chars)
    );
    return () => {
      detachOcrLineDrop();
      window.clearTimeout(queryTimer.current);
      queryId.current += 1;
      selectionId.current += 1;
    };
  }, []);
  const search = (input) => {
    const id = ++queryId.current;
    const places = client();
    if (!input || !places) {
      setSuggestions([]);
      setActivePlaceId(null);
      return;
    }
    places.autocomplete.getPlacePredictions(
      { input, sessionToken: places.sessionToken, types },
      (predictions) => {
        if (id !== queryId.current) {
          return;
        }
        const next = (predictions != null ? predictions : []).map((prediction) => ({
          description: prediction.description,
          placeId: prediction.place_id,
          matchedSubstring: prediction.matched_substrings[0]
        }));
        setSuggestions(next);
        setActivePlaceId(
          (id2) => next.some((suggestion) => suggestion.placeId === id2) ? id2 : null
        );
      }
    );
  };
  const showSuggestions = (input) => {
    search(input);
    setIsHidden(false);
  };
  const hideSuggestions = (input) => {
    window.clearTimeout(queryTimer.current);
    if (input !== selectedText.current) {
      nonSelect.requestChange(input);
    }
    setIsHidden(true);
    setActivePlaceId(null);
  };
  const select = (suggestion) => {
    window.clearTimeout(queryTimer.current);
    queryId.current += 1;
    const id = ++selectionId.current;
    selectedText.current = suggestion.description;
    setUserInput(suggestion.description);
    setIsHidden(true);
    setActivePlaceId(null);
    const places = client();
    if (!places) {
      return;
    }
    places.details.getDetails(
      {
        placeId: suggestion.placeId,
        fields: PLACE_DETAIL_FIELDS,
        sessionToken: places.sessionToken
      },
      (result, status) => {
        var _a2;
        if (id !== selectionId.current) {
          return;
        }
        if (status !== places.places.PlacesServiceStatus.OK || !((_a2 = result == null ? void 0 : result.geometry) == null ? void 0 : _a2.location)) {
          selectedText.current = null;
          nonSelect.requestChange(suggestion.description);
          return;
        }
        const { location } = result.geometry;
        places.sessionToken = new places.places.AutocompleteSessionToken();
        onSuggestionSelected(__spreadProps(__spreadValues({}, suggestion), {
          gmaps: result,
          location: { lat: location.lat(), lng: location.lng() }
        }));
      }
    );
  };
  const moveActive = (step) => {
    var _a2, _b2;
    if (isHidden) {
      showSuggestions(userInput);
      return;
    }
    const index = suggestions.findIndex((s) => s.placeId === activePlaceId);
    const next = index === -1 ? step === 1 ? 0 : suggestions.length - 1 : index + step;
    setActivePlaceId((_b2 = (_a2 = suggestions[next]) == null ? void 0 : _a2.placeId) != null ? _b2 : null);
  };
  const activeSuggestion = isHidden ? void 0 : suggestions.find((s) => s.placeId === activePlaceId);
  const handleChange = (event) => {
    const value = event.target.value;
    selectedText.current = null;
    queryId.current += 1;
    selectionId.current += 1;
    if (!value) {
      geosuggestClear.requestChange();
      hideSuggestions("");
    }
    setUserInput(value);
    window.clearTimeout(queryTimer.current);
    queryTimer.current = window.setTimeout(
      () => showSuggestions(value),
      QUERY_DELAY_MS
    );
  };
  const handleKeyDown = (event) => {
    if (isImeKey(event.nativeEvent)) {
      onKeyDown == null ? void 0 : onKeyDown(event);
      return;
    }
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
        if (!event.shiftKey) {
          event.preventDefault();
          moveActive(event.key === "ArrowDown" ? 1 : -1);
        }
        break;
      case "Enter":
        if (activeSuggestion) {
          event.preventDefault();
          select(activeSuggestion);
        } else if (userInput !== selectedText.current && userInput !== (initialValue != null ? initialValue : "")) {
          nonSelect.requestChange(userInput);
        }
        break;
      case "Tab":
        if (activeSuggestion) {
          select(activeSuggestion);
        }
        break;
      case "Escape":
        hideSuggestions(userInput);
        break;
    }
    onKeyDown == null ? void 0 : onKeyDown(event);
  };
  const listHidden = isHidden || suggestions.length === 0;
  const optionId = (suggestion) => `${listId}__${suggestion.placeId}`;
  return /* @__PURE__ */ jsxs("div", { className: "geosuggest", children: [
    /* @__PURE__ */ jsx("div", { className: "geosuggest__input-wrapper", children: /* @__PURE__ */ jsx(
      "input",
      {
        ref: inputRef,
        className: "geosuggest__input",
        "data-component": "GeosuggestDroppableInput",
        type: (_a = props.type) != null ? _a : "text",
        placeholder: (_b = props.placeholder) != null ? _b : "Search places",
        disabled: props.disabled,
        autoComplete: "nope",
        role: "combobox",
        "aria-autocomplete": "list",
        "aria-expanded": !listHidden,
        "aria-controls": listId,
        "aria-activedescendant": activeSuggestion ? optionId(activeSuggestion) : void 0,
        value: userInput,
        onChange: handleChange,
        onFocus: () => showSuggestions(userInput),
        onBlur: () => {
          if (!ignoreBlur.current) {
            hideSuggestions(userInput);
          }
        },
        onKeyDown: handleKeyDown
      }
    ) }),
    /* @__PURE__ */ jsx("div", { className: "geosuggest__suggests-wrapper", children: /* @__PURE__ */ jsx(
      "ul",
      {
        className: listHidden ? "geosuggest__suggests geosuggest__suggests--hidden" : "geosuggest__suggests",
        role: "listbox",
        id: listId,
        "aria-hidden": listHidden,
        children: suggestions.map((suggestion) => {
          const isActive = suggestion === activeSuggestion;
          return /* @__PURE__ */ jsx(
            "li",
            {
              id: optionId(suggestion),
              className: isActive ? "geosuggest__item geosuggest__item--active" : "geosuggest__item",
              role: "option",
              "aria-selected": isActive,
              onMouseDown: () => {
                ignoreBlur.current = true;
              },
              onMouseOut: () => {
                ignoreBlur.current = false;
              },
              onClick: (event) => {
                event.preventDefault();
                select(suggestion);
              },
              children: suggestionLabel(suggestion, userInput)
            },
            suggestion.placeId
          );
        })
      }
    ) })
  ] });
}
export default GeosuggestDroppableInput;
